import {
  Box,
  Button,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Container,
  Backdrop,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import {useEffect, useState} from "react";
import { useNavigate } from "react-router";
import useCompletionDialog from "components/CompletionDialog";
import { getJaDisplayName } from "components/factoringUtil";
import FilePreview from "components/FileUpload/FilePreview";
import { useFactoring } from "../FactoringProvider";
import { createFactoring } from "../createFactoring";
import { useAuthenticatedUser } from "../../../AuthProvider";
import formatPostalCode from "components/formatPostalCode";
import { tryReplaceToSlash } from "components/dateUtil";

export default function FactoringConfirm() {
  const navigate = useNavigate();

  const { factoring, entries } = useFactoring();

  const { openCompletionDialog, renderCompletionDialog } =
    useCompletionDialog("../reset");
  const user = useAuthenticatedUser();

  // エラー表示のための状態を追加
  const [error, setError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async () => {
    try {
      setLoading(true);

      await createFactoring(
        user.uid,
        factoring,
        entries,
      );

      openCompletionDialog();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "ファクタリング申込中にエラーが発生しました。",
      );
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  // エラーメッセージを閉じる処理
  const handleCloseError = () => {
    setShowError(false);
  };

  const onBack = () => {
    navigate("/factoring/entry", { state: { from: "confirm" } });
  };

  // 金額表記をフォーマットする関数
  const formatAmount = (amount: number, inTenThousand: boolean = false) => {
    try {
      return `${amount.toLocaleString()}${inTenThousand ? "万円" : "円"}`;
    } catch (e) {
      return "-";
    }
  };

  return (
    <Container
      component="main"
      maxWidth="md"
      sx={{
        mt: 4,
        mb: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
        }}
      >
        <Typography
          component="h1"
          variant="h5"
          sx={{ mb: 2, textAlign: "center" }}
        >
          申込内容の確認
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          以下の内容で申込を行います。内容をご確認の上、「申込する」ボタンを押してください。
        </Typography>

        {/* エラーメッセージを表示するSnackbar */}
        <Snackbar
          open={showError}
          autoHideDuration={6000}
          onClose={handleCloseError}
        >
          <Alert
            onClose={handleCloseError}
            severity="error"
            sx={{ width: "100%" }}
          >
            {error}
          </Alert>
        </Snackbar>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {getJaDisplayName("desiredAmount")}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List dense>
            <ListItem>
              <ListItemText
                primary={getJaDisplayName("desiredAmount")}
                secondary={formatAmount(factoring.desiredAmount, true)}
              />
            </ListItem>
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            請求情報
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {entries.length === 0 && (
            <Typography variant="body2" sx={{ p: 2, textAlign: "center" }}>
              請求情報がありません
            </Typography>
          )}

          {entries.map((entry, index) => (
            <Box key={entry.id} sx={{ mb: index < entries.length - 1 ? 3 : 0 }}>
              {index > 0 && <Divider sx={{ my: 2 }} />}
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                請求情報 {index + 1}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={getJaDisplayName("companyName")}
                    secondary={entry.companyName}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={getJaDisplayName("corporateNumber")}
                    secondary={entry.corporateNumber}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={getJaDisplayName("postalCode")}
                    secondary={formatPostalCode(entry.postalCode)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={getJaDisplayName("prefecture")}
                    secondary={entry.prefecture}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={getJaDisplayName("city")}
                    secondary={entry.city}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={getJaDisplayName("address1")}
                    secondary={entry.address1}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={getJaDisplayName("address2")}
                    secondary={entry.address2}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={getJaDisplayName("billingAmount")}
                    secondary={formatAmount(entry.billingAmount)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={getJaDisplayName("paymentDate")}
                    secondary={tryReplaceToSlash(entry.paymentDate)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={getJaDisplayName("isInvoiceIssued")}
                    secondary={entry.isInvoiceIssued ? "請求済" : "未請求"}
                  />
                </ListItem>
              </List>

              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <FilePreview title={getJaDisplayName("images")} files={entry.imageFiles} />
              </Paper>
            </Box>
          ))}
        </Paper>

        {/* 備考欄 */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {getJaDisplayName("notes")}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List dense>
            <ListItem>
              <ListItemText
                primary={getJaDisplayName("notes")}
                secondary={factoring.notes && factoring.notes.trim() !== "" ? factoring.notes : "(未入力)"}
                slotProps={{
                  secondary: {
                    typography: {
                      whiteSpace: 'pre-line'
                    }
                  },
                }}
              />
            </ListItem>
          </List>
        </Paper>

        {/* 個人情報の同意 */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {getJaDisplayName("personalInfoConsent")}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List dense>
            <ListItem>
              <ListItemText
                primary={getJaDisplayName("personalInfoConsent")}
                secondary={factoring.personalInfoConsent ? "同意済" : "未同意"}
              />
            </ListItem>
          </List>
        </Paper>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 3,
            mb: 2,
          }}
        >
          <Button onClick={onBack} variant="outlined">
            戻る
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            申込する
          </Button>
        </Box>
      </Box>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {renderCompletionDialog()}
    </Container>
  );
}
